// src/PrivacyPolicyPage.js
import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';

function PrivacyPolicyPage() {
    // Set Document Title
    useEffect(() => {
        document.title = 'SpinnerCS - Privacy Policy';
    }, []);

    return (
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ bgcolor: 'background.paper', p: { xs: 2, sm: 3, md: 4 }, borderRadius: 2 }}>
                <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ mb: 3 }}>
                    Privacy Policy
                </Typography>

                <Typography variant="body1" paragraph>
                    <strong>Last Updated:</strong> [Date] {/* TODO: Replace with the date of the last update */}
                </Typography>

                <Typography variant="body1" paragraph>
                    Welcome to SpinnerCS ("we," "us," or "our"). We are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website, services, and applications (collectively, the "Service"). Please read this policy carefully. By using the Service, you agree to the collection and use of information in accordance with this policy and our{' '}
                    <Link component={RouterLink} to="/terms" underline="hover">
                        Terms of Service
                    </Link>.
                </Typography>

                {/* Section 1 */}
                <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 3 }}>
                    1. Information We Collect
                </Typography>
                <Typography variant="body1" paragraph>
                    We may collect information about you in a variety of ways. The information we may collect via the Service includes:
                </Typography>
                <Typography component="ul" sx={{ pl: 2 }}>
                    <Typography component="li" variant="body1" paragraph>
                        <strong>Steam Account Information:</strong> When you log in using your Steam account, we receive certain information from Steam, such as your public Steam ID, display name (persona name), and avatar URL. We do not receive your Steam password or private account details.
                    </Typography>
                    <Typography component="li" variant="body1" paragraph>
                        <strong>Trade URL:</strong> If you provide it, we collect your Steam Trade URL solely for the purpose of facilitating the withdrawal of virtual items ("Items") you win through the Service to your Steam account.
                    </Typography>
                    <Typography component="li" variant="body1" paragraph>
                        <strong>Usage Data:</strong> We may automatically collect information about your interaction with the Service, such as your IP address, browser type, operating system, access times, pages viewed, actions taken within the Service (like spins performed, offers attempted), and referring website addresses.
                    </Typography>
                    <Typography component="li" variant="body1" paragraph>
                        <strong>Information from Third-Party Offer Providers:</strong> When you interact with third-party offers (e.g., through offer walls) to earn virtual "Spins," the Offer Provider may share information with us regarding the status of offer completion to credit Spins to your account. We do not control what information the Offer Provider collects from you directly.
                    </Typography>
                    <Typography component="li" variant="body1" paragraph>
                        <strong>Cookies and Tracking Technologies:</strong> We may use cookies, web beacons, and other tracking technologies to collect information about your use of the Service, enhance user experience, and analyze traffic. You can control the use of cookies at the individual browser level.
                    </Typography>
                </Typography>

                {/* Section 2 */}
                <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 3 }}>
                    2. How We Use Your Information
                </Typography>
                <Typography variant="body1" paragraph>
                    We use the information we collect for various purposes, including to:
                </Typography>
                <Typography component="ul" sx={{ pl: 2 }}>
                    <Typography component="li" variant="body1" paragraph>
                        Provide, operate, and maintain the Service.
                    </Typography>
                    <Typography component="li" variant="body1" paragraph>
                        Create and manage your account.
                    </Typography>
                    <Typography component="li" variant="body1" paragraph>
                        Process transactions, including crediting Spins and facilitating Item withdrawals via Steam Trade Offers.
                    </Typography>
                    <Typography component="li" variant="body1" paragraph>
                        Improve, personalize, and expand the Service.
                    </Typography>
                    <Typography component="li" variant="body1" paragraph>
                        Understand and analyze how you use the Service.
                    </Typography>
                    <Typography component="li" variant="body1" paragraph>
                        Monitor and prevent fraud, abuse, and security issues.
                    </Typography>
                    <Typography component="li" variant="body1" paragraph>
                        Communicate with you, potentially including service announcements or support responses (if you contact us).
                    </Typography>
                    <Typography component="li" variant="body1" paragraph>
                        Comply with legal obligations.
                    </Typography>
                </Typography>

                {/* Section 3 */}
                <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 3 }}>
                    3. Disclosure of Your Information
                </Typography>
                <Typography variant="body1" paragraph>
                    We may share information we have collected about you in certain situations:
                </Typography>
                <Typography component="ul" sx={{ pl: 2 }}>
                    <Typography component="li" variant="body1" paragraph>
                        <strong>With Third-Party Service Providers:</strong> We may share your information with third-party vendors, consultants, and other service providers who perform services for us or on our behalf (e.g., hosting, data analysis, offer wall providers).
                    </Typography>
                    <Typography component="li" variant="body1" paragraph>
                        <strong>With Steam (Valve Corporation):</strong> Information necessary to facilitate login and Trade Offers is inherently shared with or processed through Steam's systems. Your use of Steam is subject to Valve's Privacy Policy.
                    </Typography>
                    <Typography component="li" variant="body1" paragraph>
                        <strong>For Legal Reasons:</strong> We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., a court or government agency).
                    </Typography>
                    <Typography component="li" variant="body1" paragraph>
                        <strong>To Protect Rights:</strong> We may disclose information where we believe it is necessary to investigate, prevent, or take action regarding potential violations of our policies, suspected fraud, situations involving potential threats to the safety of any person, illegal activities, or as evidence in litigation.
                    </Typography>
                    <Typography component="li" variant="body1" paragraph>
                        <strong>Business Transfers:</strong> In connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business by another company, your information may be transferred.
                    </Typography>
                    {/* TODO: Add/remove clauses based on actual practices */}
                </Typography>
                <Typography variant="body1" paragraph>
                    We do not sell your personal information. {/* TODO: Verify this statement is accurate */}
                </Typography>

                {/* Section 4 */}
                <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 3 }}>
                    4. Third-Party Services
                </Typography>
                <Typography variant="body1" paragraph>
                    The Service uses Steam for authentication and may integrate with third-party Offer Providers. These third-party services have their own privacy policies. We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies.
                </Typography>

                {/* Section 5 */}
                <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 3 }}>
                    5. Data Security
                </Typography>
                <Typography variant="body1" paragraph>
                    We implement reasonable administrative, technical, and physical security measures designed to protect your information from unauthorized access, use, or disclosure. However, no electronic transmission over the Internet or information storage technology can be guaranteed to be 100% secure.
                </Typography>

                {/* Section 6 */}
                <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 3 }}>
                    6. Data Retention
                </Typography>
                <Typography variant="body1" paragraph>
                    We will retain your information only for as long as is necessary for the purposes set out in this Privacy Policy, or as required by law. We will retain and use your information to the extent necessary to comply with our legal obligations, resolve disputes, and enforce our policies.
                    {/* TODO: Be more specific if possible/required */}
                </Typography>

                {/* Section 7 */}
                <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 3 }}>
                    7. Your Data Protection Rights (Example: GDPR/CCPA)
                </Typography>
                <Typography variant="body1" paragraph>
                    Depending on your location, you may have certain rights regarding your personal information, such as the right to access, correct, delete, or restrict the processing of your data.
                    {/* TODO: This section MUST be tailored based on applicable laws (GDPR, CCPA, etc.) and your ability to fulfill requests. Consult legal counsel. */}
                </Typography>
                <Typography variant="body1" paragraph>
                    If you wish to exercise any of these rights, please contact us using the contact information below. We may need to verify your identity before responding to such requests.
                </Typography>

                {/* Section 8 */}
                <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 3 }}>
                    8. Children's Privacy
                </Typography>
                <Typography variant="body1" paragraph>
                    The Service is not intended for use by children under the age of 13 (or a higher age threshold if required by applicable law). We do not knowingly collect personal information from children under this age. If we become aware that we have collected personal information from a child without verification of parental consent, we take steps to remove that information.
                    {/* TODO: Verify age limit aligns with Steam's terms and applicable laws (e.g., COPPA, GDPR). */}
                </Typography>

                {/* Section 9 */}
                <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 3 }}>
                    9. Changes to This Privacy Policy
                </Typography>
                <Typography variant="body1" paragraph>
                    We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes. Changes are effective when they are posted on this page.
                </Typography>

                {/* Section 10 */}
                <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 3 }}>
                    10. Contact Us
                </Typography>
                <Typography variant="body1" paragraph>
                    If you have any questions about this Privacy Policy, please contact us at: [Your Support Email Address or Contact Method]
                    {/* TODO: Replace placeholder with your actual contact info */}
                </Typography>
            </Box>
        </Container>
    );
}

export default PrivacyPolicyPage;
